import type { OptionalFollowUpConsultCondition } from '@common/customControls/tooV1Diagnose';
import type { PatientInsuranceHistory } from '@common/interfaces/api/patientsApi';
import type { ITooProcedureCustomControlData } from '@common/validation/customControls/ophthalmology';
import { isNonNullable } from '@common/utils/nonNullableTypeUtils';
import { uzoviRegister } from '@common/uzoviRegister';
import { OphthalmologyFilePlacement } from '@common/validation/customControls/ophthalmology';
import { getFilesByPlacement } from '@frontend/components/pages/customControls/TFO';
import { fetchJson } from '@frontend/store/fetch';

export async function requestServiceConditionals(
    patientId: string,
    organizationId: string | undefined | null,
    practitionerId: string | undefined | null
): Promise<{
    careGroupHasTOOFinancingAgreement: boolean;
    optionalFollowUpConsultConditions: OptionalFollowUpConsultCondition[];
}> {
    const agreementsUrl = `/api/patients/${patientId}/serviceConditionals`;
    const queryParams = [
        ['organizationId', organizationId],
        ['practitionerId', practitionerId],
    ].filter(([_, v]) => isNonNullable(v)) as string[][];

    const queryString = new URLSearchParams(queryParams).toString();
    const agreementsResult = await fetchJson(`${agreementsUrl}?${queryString}`, 'GET');

    return agreementsResult.json.serviceConditionals;
}

export function checkIsReferralCheckRequired({
    activeInsurance,
    isDirectProcedureAdded,
}: {
    activeInsurance: PatientInsuranceHistory | null;
    isDirectProcedureAdded: boolean;
}): boolean {
    // When a general practitioner has started the request, no referral check required
    if (!isDirectProcedureAdded) {
        return false;
    }

    // Check if patient insurance requires a referral check
    const uzoviCodesWhereReferralIsRequired: string[] = [
        uzoviRegister.anderZorg.code,
        uzoviRegister.cz.code,
        uzoviRegister.menzis.code,
    ];
    if (activeInsurance) {
        if (uzoviCodesWhereReferralIsRequired.includes(activeInsurance.uzovi.code)) {
            return true;
        }
    }

    return false;
}

export function checkIsGeneralPractitionerInvolvementRequired({
    isDirectProcedureAdded,
    isReferralCheckRequired,
    organizationId,
    practitionerId,
}: {
    isDirectProcedureAdded: boolean;
    isReferralCheckRequired: boolean;
    organizationId: string | undefined | null;
    practitionerId: string | undefined | null;
}): boolean {
    if (!isDirectProcedureAdded) {
        // procedure is started by general practitioner
        return false;
    }

    // referral check required excludes general practitioner involvement
    if (isReferralCheckRequired) {
        return false;
    }

    // if one is not yet known, return false
    return Boolean(organizationId && practitionerId);
}

export async function checkIsReferralCheckCompleted({
    customControlData,
    isDirectProcedureAdded,
    isReferralCheckRequired,
    organizationId,
    patientId,
    practitionerId,
}: {
    customControlData: ITooProcedureCustomControlData;
    isDirectProcedureAdded: boolean;
    isReferralCheckRequired: boolean;
    organizationId: string | undefined | null;
    patientId: string;
    practitionerId: string | undefined | null;
}): Promise<boolean> {
    if (isReferralCheckRequired) {
        // When the care group has financial agreements the referral check is completed
        if (organizationId && practitionerId) {
            const { careGroupHasTOOFinancingAgreement } = await requestServiceConditionals(
                patientId,
                organizationId,
                practitionerId
            );
            if (careGroupHasTOOFinancingAgreement) {
                return true;
            }
        }

        // When the referral letter is uploaded the referral check is completed
        return !!getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.verwijsbrief).length;
    } else {
        return (
            customControlData.huisarts_is_betrokken !== null ||
            customControlData.huisarts_betrokkenheid !== null ||
            !isDirectProcedureAdded
        );
    }
}

export function getIsReferralCheckCompleted({
    customControlData,
    hasCareGroupTOOFinancingAgreement,
    isDirectProcedureAdded,
    isReferralCheckRequired,
}: {
    customControlData: ITooProcedureCustomControlData;
    hasCareGroupTOOFinancingAgreement: boolean;
    isDirectProcedureAdded: boolean;
    isReferralCheckRequired: boolean;
}): boolean {
    if (isReferralCheckRequired) {
        return (
            hasCareGroupTOOFinancingAgreement ||
            // When the referral letter is uploaded the referral check is completed
            !!getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.verwijsbrief).length
        );
    }

    return (
        customControlData.huisarts_is_betrokken !== null ||
        customControlData.huisarts_betrokkenheid !== null ||
        !isDirectProcedureAdded
    );
}
